import OnlyHeader from "components/Layout/OnlyHeader";
import React, { useEffect, useRef, useState } from "react";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Popover,
  PopoverBody,
  Row,
  Spinner,
} from "reactstrap";
import CategoryModal from "./CategoryModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories } from "store/actions/categoriesActions";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { deleteCategory } from "store/actions/categoriesActions";
import { toggleCategoryVisibility } from "store/actions/categoriesActions";
import { duplicateCategory } from "store/actions/categoriesActions";
import csvtojson from "csvtojson";
import { toast } from "react-toastify";
import { addItem } from "store/actions/itemsActions";
import usePermissions from "hooks/usePermissions";

const Categories = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { user, userPermissions } = useSelector((state) => state.auth);
  const { id: menuId } = useParams();
  const { categories } = useSelector((state) => state.category);

  const popoverRef = useRef(null);
  const fileRef = useRef(null);

  const [filteredCategories, setFilteredCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [categoryModal, setCategoryModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [visibilityModal, setVisibilityModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(null);
  const {
    add: addAccess,
    edit: editAccess,
    delete: deleteAccess,
    get: getAccess,
    export: exportAccess,
    visibility: visibilityAccess,
    duplicate: duplicateAccess,
  } = usePermissions();

  const hasItems =
    user?.type == "restaurant" ||
    userPermissions?.menus?.category?.items?.active;
  const hasItemsAccess =
    duplicateAccess ||
    visibilityAccess ||
    editAccess ||
    deleteAccess ||
    exportAccess;
  const togglePopover = (id) => setPopoverOpen(popoverOpen === id ? null : id);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearch(query);

    const filtered = categories.filter((category) =>
      category.name.toLowerCase().includes(query)
    );
    setFilteredCategories(filtered);
  };

  const categoryModalToggler = () => {
    if (categoryModal) {
      setActiveCategory(null);
      setEditMode(false);
    }
    setCategoryModal(!categoryModal);
  };

  const duplicateModalToggler = () => {
    if (duplicateModal) {
      setActiveCategory(null);
    }
    setDuplicateModal(!duplicateModal);
  };

  const visibilityModalToggler = () => {
    if (visibilityModal) {
      setActiveCategory(null);
    }
    setVisibilityModal(!visibilityModal);
  };

  const importModalToggler = () => {
    if (importModal) {
      setFile(null);
    }
    setImportModal(!importModal);
  };

  const confirmationModalToggler = () => {
    if (confirmationModal) {
      setActiveCategory(null);
    }
    setConfirmationModal(!confirmationModal);
  };

  const initiateUpdateCategory = (category) => {
    setPopoverOpen(null);
    setActiveCategory(category);
    setEditMode(true);
    categoryModalToggler();
  };

  const initiateDuplicate = (category) => {
    setPopoverOpen(null);
    setActiveCategory(category);
    duplicateModalToggler();
  };

  const initiateToggleVisibility = (category) => {
    setPopoverOpen(null);
    setActiveCategory(category);
    visibilityModalToggler();
  };

  const initiateDelete = (category) => {
    setPopoverOpen(null);
    setActiveCategory(category);
    confirmationModalToggler();
  };

  const initiateImport = (category) => {
    setPopoverOpen(null);
    setActiveCategory(category);
    importModalToggler();
  };

  const handleDuplicateCategory = () => {
    setIsLoading(true);
    dispatch(
      duplicateCategory({
        restaurantID: user?.restaurantID,
        categoryData: activeCategory,
      })
    )
      .then(() => duplicateModalToggler())
      .finally(() => setIsLoading(false));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const fileName = file?.name?.split(".")[1];
    if (!file) return;

    if (fileName === "csv") {
      setFile(file);
    } else {
      toast.error("Select a valid CSV file");
    }
    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  const handleImportItems = () => {
    const reader = new FileReader();
    reader.readAsText(file);

    reader.onload = async () => {
      setIsLoading(true);
      try {
        const importedData = await csvtojson().fromString(reader.result);

        if (importedData?.length > 0) {
          const promises = importedData.map(async (data) => {
            let variants = [];
            let sizesColumns = data?.Sizes_price_calories.split(",");
            for (let singleSize of sizesColumns) {
              let ItemsSizes = singleSize?.split("_");
              let obj = {
                size: ItemsSizes[0],
                price: ItemsSizes[1],
                calories: ItemsSizes[2],
              };
              variants.push({ ...obj });
            }

            let item = {
              name: data?.itemName,
              estimatedTime: data?.EstTime,
              restaurantID: user?.restaurantID,
              variants,
              recommendedProducts: [],
              label: [],
              images: [],
              comments: "",
              menuID: menuId,
            };

            await dispatch(
              addItem({ categoryId: activeCategory?.id, payload: item })
            );
          });

          await Promise.all(promises);

          toast.success("Items imported successfully!");
          importModalToggler();
        }
      } catch (err) {
        console.error(err.message);
        toast.error(err.message || "Unable to import items");
      } finally {
        setIsLoading(false);
      }
    };
  };

  const handleDeleteCategory = () => {
    setIsLoading(true);
    dispatch(deleteCategory(activeCategory?.id, activeCategory?.image))
      .then(() => {
        toast.success("Category Deleted Successfully");
        confirmationModalToggler();
      })
      .finally(() => setIsLoading(false));
  };

  const handleToggleCategoryVisibility = async () => {
    setIsLoading(true);
    dispatch(
      toggleCategoryVisibility(activeCategory?.id, !activeCategory?.isVisible)
    )
      .then(() => visibilityModalToggler())
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (user?.restaurantID) {
      setIsFetching(true);
      dispatch(fetchCategories(user?.restaurantID, menuId)).finally(() =>
        setIsFetching(false)
      );
    }
  }, [dispatch, menuId, user]);

  useEffect(() => {
    setFilteredCategories(categories);
  }, [categories]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !event.target.closest(".popover")
      ) {
        setTimeout(() => setPopoverOpen(null), 0);
      }
    };

    if (popoverOpen !== null) {
      document.addEventListener("pointerdown", handleClickOutside);
    } else {
      document.removeEventListener("pointerdown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [popoverOpen]);

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center px-2 px-lg-4">
                <Col xs="12" lg="3" xl="6" className="mb-3 mb-lg-0">
                  <div className="d-flex align-items-center">
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => history.goBack()}
                    >
                      <i className="fas fa-arrow-left " />
                    </Button>

                    <h3 className="mb-0">Categories</h3>
                  </div>
                </Col>
                <Row className="w-100 mx-0 justify-content-end">
                  <Col xs="12" sm="8" lg="8" className="mb-3 mb-sm-0">
                    <Input
                      placeholder="Search by category name"
                      value={search}
                      onChange={handleSearch}
                    />
                  </Col>
                  {addAccess && (
                    <Col xs="12" sm="4" lg="4">
                      <Button
                        className="w-100"
                        color="primary"
                        onClick={categoryModalToggler}
                      >
                        Add
                      </Button>
                    </Col>
                  )}
                </Row>
              </CardHeader>

              <CardBody>
                {isFetching ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : getAccess && filteredCategories?.length ? (
                  <Row className="p-0 p-lg-3">
                    {filteredCategories?.map(
                      (category, index) => (
                        (
                          <Col
                            key={category?.id}
                            xl={4}
                            lg={4}
                            md={6}
                            sm={6}
                            xs={12}
                            className="mb-4"
                          >
                            <Card className="w-100 h-100 shadow-sm overflow-hidden border border-gray">
                              <CardHeader
                                className="w-100 p-0 overflow-hidden"
                                style={{ height: "175px" }}
                              >
                                {!category?.isVisible && (
                                  <Badge
                                    color="danger"
                                    className="position-absolute top-2 right-2 bg-danger text-white shadow"
                                  >
                                    Hidden
                                  </Badge>
                                )}
                                <img
                                  src={category?.image}
                                  className="w-100 h-100"
                                  style={{ objectFit: "cover" }}
                                  alt="category img"
                                />
                              </CardHeader>
                              <CardBody className="px-3 py-2  bg-secondary">
                                <CardTitle tag="h3" className="mb-0 p-0">
                                  {category?.name}
                                </CardTitle>
                              </CardBody>
                              <CardFooter className="p-0 bg-secondary d-flex">
                                {/* Left 75% - View Items */}
                                {hasItems && (
                                  <div
                                    className={`d-flex align-items-center ${
                                      hasItemsAccess ? "w-75" : "w-100"
                                    }  p-2`}
                                  >
                                    <Button
                                      size="sm"
                                      className="w-100 d-flex align-items-center justify-content-center"
                                      color="success"
                                      onClick={() =>
                                        history.push(
                                          `/admin/menus/${menuId}/categories/${category?.id}/items`
                                        )
                                      }
                                    >
                                      <i className="fa fa-eye mr-2" />
                                      VIEW ITEMS
                                    </Button>
                                  </div>
                                )}

                                {/* Right 25% - Ellipsis Button + Popover */}
                                {hasItemsAccess && (
                                  <div
                                    className="w-25 d-flex align-items-center justify-content-center px-2 m-2"
                                    ref={popoverRef}
                                  >
                                    <div>
                                      <Button
                                        id={
                                          category?.id &&
                                          `popover-${category.id}`
                                        }
                                        size="sm"
                                        className="p-0 "
                                        color="primary"
                                        style={{
                                          width: "60px",
                                          height: "26px",
                                          color: "white",
                                        }}
                                      >
                                        <i className="fas fa-ellipsis-h" />
                                      </Button>
                                      <Popover
                                        placement="bottom-end"
                                        isOpen={popoverOpen === category.id}
                                        target={`popover-${category.id}`}
                                        toggle={() =>
                                          togglePopover(category.id)
                                        }
                                      >
                                        <PopoverBody className="p-2">
                                          <div className="d-flex flex-column">
                                            {visibilityAccess && (
                                              <Button
                                                className="bg-purple text-white d-flex align-items-center w-100 m-0 mb-2 px-2"
                                                size="sm"
                                                onClick={() =>
                                                  initiateToggleVisibility(
                                                    category
                                                  )
                                                }
                                              >
                                                <i className="fa fa-eye mr-2" />
                                                Visibility
                                              </Button>
                                            )}
                                            {editAccess && (
                                              <Button
                                                size="sm"
                                                color="warning"
                                                className="d-flex align-items-center w-100 m-0 mb-2 px-2"
                                                onClick={() =>
                                                  initiateUpdateCategory(
                                                    category
                                                  )
                                                }
                                              >
                                                <i className="fa fa-edit mr-2" />
                                                Edit
                                              </Button>
                                            )}
                                            {deleteAccess && (
                                              <Button
                                                size="sm"
                                                color="danger"
                                                className="d-flex align-items-center w-100 m-0 mb-2 px-2"
                                                onClick={() =>
                                                  initiateDelete(category)
                                                }
                                              >
                                                <i className="fa fa-trash mr-2" />
                                                Delete
                                              </Button>
                                            )}
                                            {duplicateAccess && (
                                              <Button
                                                size="sm"
                                                className="d-flex align-items-center w-100 m-0 mb-2 px-2"
                                                color="primary"
                                                onClick={() =>
                                                  initiateDuplicate(category)
                                                }
                                              >
                                                <i className="fa fa-copy mr-2" />
                                                Duplicate
                                              </Button>
                                            )}
                                            {exportAccess && (
                                              <Button
                                                size="sm"
                                                color="dark"
                                                className="d-flex align-items-center w-100 m-0 mb-2 px-2"
                                                onClick={() =>
                                                  initiateImport(category)
                                                }
                                              >
                                                <i className="fa-solid fa-file-import mr-2" />
                                                Import
                                              </Button>
                                            )}
                                          </div>
                                        </PopoverBody>
                                      </Popover>
                                    </div>
                                  </div>
                                )}
                              </CardFooter>
                            </Card>
                          </Col>
                        )
                      )
                    )}
                  </Row>
                ) : (
                  <div className="d-flex flex-column justify-content-center align-items-center py-5">
                    No Category found
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </Row>
        <input
          id="importFile"
          ref={fileRef}
          type="file"
          hidden
          onChange={handleFileChange}
        />
      </Container>
      {categoryModal && (
        <CategoryModal
          isOpen={categoryModal}
          toggle={categoryModalToggler}
          menuId={menuId}
          editMode={editMode}
          activeCategory={activeCategory}
        />
      )}
      <ConfirmationModal
        title="Import Items"
        text={
          <>
            <h3>Are you sure you want to import items to this category?</h3>
            <span style={{ fontSize: "14px" }}>
              <strong>Note: </strong>
              This will also add all valid items from the csv file in this
              category.
            </span>
            <h5 className="mt-3">Upload File:</h5>
            <label
              htmlFor="importFile"
              className="border border-light rounded w-100 cursor-pointer bg-lighter d-flex align-items-center px-3"
              style={{ height: "45px" }}
            >
              {file ? file?.name : "Upload a .csv file"}
            </label>
            <a
              href="https://firebasestorage.googleapis.com/v0/b/nexteats-staging.appspot.com/o/Sample%20CSV%20(8).csv?alt=media&token=4cfa5dee-da83-4143-9c23-c7d79392c778"
              style={{
                color: "#0d6efd",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              DOWNLOAD SAMPLE CSV
            </a>
          </>
        }
        isOpen={importModal}
        toggle={importModalToggler}
        isLoading={isLoading}
        onConfirmation={handleImportItems}
      />
      <ConfirmationModal
        title="Duplicate Category"
        text={
          <>
            <h3>Are you sure you want to duplicate this category?</h3>
            <span style={{ fontSize: "14px" }}>
              <strong>Note: </strong>
              This will also duplicate items against this category.
            </span>
          </>
        }
        isOpen={duplicateModal}
        toggle={duplicateModalToggler}
        isLoading={isLoading}
        onConfirmation={handleDuplicateCategory}
      />
      <ConfirmationModal
        title="Toggle Category Visibility"
        text={
          <>
            <h3>
              Are you sure you want to{" "}
              {!activeCategory?.isVisible ? "show" : "hide"} this category?
            </h3>
            <span style={{ fontSize: "14px" }}>
              <strong>Note: </strong>
              This category will {activeCategory?.isVisible && "not"} be
              visibile to customer on main site.
            </span>
          </>
        }
        isOpen={visibilityModal}
        toggle={visibilityModalToggler}
        isLoading={isLoading}
        onConfirmation={handleToggleCategoryVisibility}
      />
      <ConfirmationModal
        title="Delete Category"
        text={
          <>
            <h3>Are you sure you want to delete this category?</h3>
            <span style={{ fontSize: "14px" }}>
              <strong>Note: </strong>
              This will delete items against this category.
            </span>
          </>
        }
        isOpen={confirmationModal}
        toggle={confirmationModalToggler}
        isLoading={isLoading}
        onConfirmation={handleDeleteCategory}
      />
    </>
  );
};

export default Categories;
